import { Popover, PopoverContent, PopoverTrigger, Typography, Button } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import type { VariableEditorPopoverProps, ScheduleValue } from "./types";
import { useVariableEditorState } from "./customHooks/useVariableEditorState";
import { RadioSelectionEditor, ThresholdEditor, ScheduleEditor, DropdownEditor } from "./editors";
import { VariableType } from "@/pages/subscriptions/constants";

const VariableEditorPopover = ({
  children,
  variable,
  onUpdate,
  open,
  onOpenChange,
  metricDefinition,
  allVariables = [],
}: VariableEditorPopoverProps) => {
  const {
    value,
    displayValue,
    validationError,
    setValue,
    setDisplayValue,
    setValidationError,
    thresholdType,
    thresholdValue,
    isAbsoluteValue,
    setThresholdType,
    setThresholdValue,
    scheduleData,
    setScheduleData,
    filteredOptions,
    isEditable,
    unit,
    validateThreshold,
    validateSchedule,
    formatScheduleDisplay,
  } = useVariableEditorState({
    variable,
    open,
    metricDefinition,
    allVariables,
  });

  const handleSave = () => {
    // Validate based on variable type
    if (variable.type === VariableType.THRESHOLD) {
      if (!validateThreshold(thresholdValue, thresholdType)) {
        return;
      }
      // Format the value based on type
      const formattedValue = thresholdType === "percentage" ? `${thresholdValue}%` : thresholdValue;
      const formattedDisplay =
        thresholdType === "percentage" ? `${thresholdValue}%` : thresholdValue;

      onUpdate({
        ...variable,
        value: formattedValue,
        displayValue: formattedDisplay,
      });
      onOpenChange?.(false);
      return;
    }

    if (variable.type === VariableType.SCHEDULE) {
      if (!validateSchedule()) {
        return;
      }
    }

    // Convert array values to string for storage
    const finalValue =
      variable.type === VariableType.SCHEDULE
        ? JSON.stringify(scheduleData)
        : Array.isArray(value)
          ? JSON.stringify(value)
          : value;

    onUpdate({
      ...variable,
      value: finalValue,
      displayValue,
    });
    onOpenChange?.(false);
  };

  const handleScheduleFrequencyChange = (freq: "daily" | "weekly" | "monthly") => {
    const newData: ScheduleValue = {
      frequency: freq,
      time: scheduleData.time,
    };

    // Initialize with defaults when switching frequency
    if (freq === "weekly") {
      newData.daysOfWeek = ["Mon"];
    } else if (freq === "monthly") {
      newData.dayOfMonth = 1;
    }

    setScheduleData(newData);
    setDisplayValue(formatScheduleDisplay(newData));
    setValidationError("");
  };

  const handleScheduleTimeChange = (time: string) => {
    const newData = { ...scheduleData, time };
    setScheduleData(newData);
    setDisplayValue(formatScheduleDisplay(newData));
  };

  const toggleDayOfWeek = (day: string) => {
    // Always single day selection - schedule determines analysis period
    // (daily schedule = analyze previous day, weekly = previous week, etc.)
    const newDays = [day];

    const newData = { ...scheduleData, daysOfWeek: newDays };
    setScheduleData(newData);
    setDisplayValue(formatScheduleDisplay(newData));
    setValidationError("");
  };

  const handleDayOfMonthChange = (day: number) => {
    const newData = { ...scheduleData, dayOfMonth: day };
    setScheduleData(newData);
    setDisplayValue(formatScheduleDisplay(newData));
    setValidationError("");
  };

  const handleValueChange = (newValue: string | string[], newDisplayValue: string) => {
    setValue(newValue);
    setDisplayValue(newDisplayValue);
  };

  const renderEditor = () => {
    // Aggregation/Comparison/Change Type/Date Range - radio selection
    if (
      variable.type === VariableType.AGGREGATION ||
      variable.type === VariableType.COMPARISON ||
      variable.type === VariableType.CHANGE_TYPE ||
      variable.type === VariableType.DATE_RANGE
    ) {
      return (
        <RadioSelectionEditor
          value={value}
          displayValue={displayValue}
          validationError={validationError}
          variableType={variable.type}
          filteredOptions={filteredOptions}
          onValueChange={handleValueChange}
          onValidationError={setValidationError}
        />
      );
    }

    // Threshold - amount or percentage
    if (variable.type === VariableType.THRESHOLD) {
      return (
        <ThresholdEditor
          thresholdType={thresholdType}
          thresholdValue={thresholdValue}
          isAbsoluteValue={isAbsoluteValue}
          unit={unit}
          validationError={validationError}
          onThresholdTypeChange={setThresholdType}
          onThresholdValueChange={setThresholdValue}
          onValidationError={setValidationError}
          onValidate={validateThreshold}
        />
      );
    }

    // Schedule - frequency and time picker with dynamic fields
    if (variable.type === VariableType.SCHEDULE) {
      return (
        <ScheduleEditor
          scheduleData={scheduleData}
          validationError={validationError}
          onFrequencyChange={handleScheduleFrequencyChange}
          onTimeChange={handleScheduleTimeChange}
          onDayOfWeekToggle={toggleDayOfWeek}
          onDayOfMonthChange={handleDayOfMonthChange}
        />
      );
    }

    // Time period (default select dropdown)
    const options = variable.config?.options || [];
    return (
      <DropdownEditor
        value={value}
        displayValue={displayValue}
        validationError={validationError}
        options={options}
        onValueChange={handleValueChange}
        onValidationError={setValidationError}
      />
    );
  };

  const getPopoverTitle = () => {
    if (variable.type === VariableType.THRESHOLD) return "Enter Amount";
    if (variable.type === VariableType.SCHEDULE) return "Schedule Settings";
    if (variable.type === VariableType.AGGREGATION) return "Aggregation Method";
    if (variable.type === VariableType.COMPARISON) return "Comparison Type";
    if (variable.type === VariableType.CHANGE_TYPE) return "Change Type";
    return "Edit Variable";
  };

  return (
    <Popover open={isEditable && open} onOpenChange={isEditable ? onOpenChange : undefined}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="al-w-[380px] al-p-4" align="start">
        <div className="al-space-y-4">
          <Stack direction="row" align="center" justify="between">
            <Typography variant="body" weight="medium" className="al-text-sm">
              {getPopoverTitle()}
            </Typography>
          </Stack>

          {renderEditor()}

          <Stack direction="row" gap="sm" justify="end" className="al-pt-3 al-border-t">
            <Button variant="outline" size="sm" onClick={() => onOpenChange?.(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              className="al-bg-blue-600 hover:al-bg-blue-700"
              disabled={!!validationError}
            >
              Save
            </Button>
          </Stack>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default VariableEditorPopover;
